import { useMutation } from '@tanstack/react-query';
import { bugReportApi } from '../api/bugReportApi';
import { CreateBugReportDTO } from '../dtos';
import { useNotifications } from '@shared/components/notifications';
import { useAuthStore } from '@features/authentication/store/authStore';

interface BugReportFormData {
  bugDescription: string;
  howItHappened: string;
  isBlocking: boolean;
  images: File[];
}

export const useBugReport = () => {
  const notifications = useNotifications();
  const { userSession } = useAuthStore();

  const submitMutation = useMutation({
    mutationFn: async (data: BugReportFormData) => {
      // Convert images to base64
      const imageUrls: string[] = [];

      for (const image of data.images) {
        try {
          const base64 = await convertFileToBase64(image);
          imageUrls.push(base64);
        } catch (error) {
          console.error('Failed to convert image:', error);
        }
      }

      // Prepare the DTO
      const dto: CreateBugReportDTO = {
        bugDescription: data.bugDescription,
        howItHappened: data.howItHappened,
        isBlocking: data.isBlocking,
        imageUrls,
        userEmail: userSession?.email,
        userName: userSession?.name || userSession?.email,
        userRole: userSession?.currentRoles?.[0],
        timestamp: new Date().toISOString(),
        environment: import.meta.env.MODE || 'development',
        userAgent: navigator.userAgent,
        currentUrl: window.location.href,
      };

      return bugReportApi.submitBugReport(dto);
    },
    onSuccess: () => {
      notifications.success('Bug report submitted successfully. Thank you for your feedback!');
    },
    onError: (error: any) => {
      console.error('Failed to submit bug report:', error);
      const errorMessage = error?.message || 'Failed to submit bug report. Please try again.';
      notifications.error(errorMessage);
    },
  });

  return {
    submitBugReport: submitMutation.mutate,
    isLoading: submitMutation.isPending,
    error: submitMutation.error
      ? (submitMutation.error as any)?.message || 'An error occurred'
      : null,
    success: submitMutation.isSuccess,
  };
};

// Helper function to convert file to base64
function convertFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
